import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated } from '../../services/authService';
import { subscriptionManagementApi } from '../../services/subscriptionManagementApi';
import PaymentRequired from '../payment/PaymentRequired';
import EmailVerificationRequired from './EmailVerificationRequired';

const PrivateRoute = ({ children, bypassPaymentCheck = false }) => {
  const isAuth = isAuthenticated();
  const location = useLocation();
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  
  useEffect(() => {
    const checkAccountStatus = async () => {
      if (!isAuth || bypassPaymentCheck) {
        setLoading(false);
        return;
      }

      try {
        const response = await subscriptionManagementApi.getAccountStatus();
        if (response.success && response.data.account) {
          setAccountStatus(response.data.account);
        } else if (response.errorCode === 'EMAIL_NOT_VERIFIED') {
          // L'email n'est pas vérifié
          setEmailNotVerified(true);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut du compte:', error);
        // En cas d'erreur, on utilise les données du localStorage comme fallback
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
          const user = JSON.parse(userInfo);
          setAccountStatus({
            status: user.account_status || 'active',
            payment_required: user.payment_required || false
          });
        }
      } finally {
        setLoading(false);
      }
    };

    checkAccountStatus();
  }, [isAuth, bypassPaymentCheck]);

  // Écouter les événements 402 remontés par l'intercepteur axios
  // pour bloquer immédiatement même si l'utilisateur est déjà sur une page
  useEffect(() => {
    const handlePaymentRequired = (e) => {
      setAccountStatus(prev => ({
        ...(prev || {}),
        status: prev?.status || 'active',
        failed_payments: e.detail?.failed_payments || 1,
      }));
    };
    window.addEventListener('payment_required', handlePaymentRequired);
    return () => window.removeEventListener('payment_required', handlePaymentRequired);
  }, []);

  if (!isAuth) {
    return <Navigate to="/login" />;
  }

  // Afficher un loading pendant la vérification
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
    </div>;
  }

  // Vérifier si l'email n'est pas vérifié (priorité maximale)
  if (emailNotVerified) {
    const userInfo = localStorage.getItem('userInfo');
    const user = userInfo ? JSON.parse(userInfo) : {};
    return <EmailVerificationRequired user={user} />;
  }

  // Vérifier si l'utilisateur a besoin d'être redirigé vers la page de paiement
  // Sauf si bypassPaymentCheck est activé (pour la page /subscription elle-même)
  if (!bypassPaymentCheck) {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      const user = JSON.parse(userInfo);
      
      // Vérifications pour rediriger vers PaymentRequired :
      // 1. payment_required est true (inscription incomplète)
      // 2. compte suspendu
      // 3. compte en période de grâce
      // 4. au moins 1 échec de paiement → blocage immédiat
      const needsPaymentAction = 
        user.payment_required === true || 
        (accountStatus && accountStatus.status === 'suspended') ||
        (accountStatus && accountStatus.status === 'grace_period') ||
        (accountStatus && accountStatus.failed_payments > 0);
      
      if (needsPaymentAction) {
        console.log('PrivateRoute - Redirection vers PaymentRequired:', {
          payment_required: user.payment_required,
          account_status: accountStatus?.status,
          failed_payments: accountStatus?.failed_payments
        });
        
        return <PaymentRequired user={{
          ...user,
          account_status: accountStatus?.status || user.account_status,
          suspension_reason: accountStatus?.suspension_reason,
          failed_payments: accountStatus?.failed_payments || 0,
        }} />;
      }
    }
  }
  
  return children;
};

export default PrivateRoute;
